import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { CheckmarkCircleRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { HoursApi } from "@/services/api/hours-api";
import { EnergyStafferDealResourceForm } from "./EnergyStafferDealResourceForm";
import { HoursTable, type HoursTableRow } from "./HoursTable";
import type { EnergyHoursProject, EnergyHoursResponse, EnergyStafferBanker } from "./weekly-hours-types";

interface EnergyStafferHoursFormProps {
  isSi: boolean;
  actorUserId?: number;
}

type StafferMode = "analyst" | "deal";

interface StafferDraftRow {
  projectId: number;
  taskId: number;
  projectName: string;
  context: string;
  first: string;
  second: string;
  third: string;
  originalFirst: number | null;
  originalSecond: number | null;
  originalThird: number | null;
  editable: boolean;
  lockedReason: string | null;
}

function toInput(value: number | null | undefined): string {
  return value == null ? "" : String(value);
}

function toNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatHours(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
}

const STAFFER_LABELS = ["Last weekday hours", "Last weekend hours", "Next seven-day estimate"] as const;
const FIELDS = ["first", "second", "third"] as const;

function currentStafferIsOwner(project: EnergyHoursProject, actorUserId?: number): boolean {
  if (!actorUserId) return false;
  return !!project.Members?.some(
    (member) => (member.User?.Id === actorUserId || member.User?.UserId === actorUserId) && member.IsOwner
  );
}

function buildRows(projects: EnergyHoursProject[], actorUserId?: number): StafferDraftRow[] {
  return projects.map((project) => {
    const isMember = project.IsProjectMember !== false;
    const isOwner = currentStafferIsOwner(project, actorUserId);
    const editable = !project.IsOverdue && isMember && isOwner;
    const lockedReason = project.IsOverdue
      ? "Extend the deal due date in TRIYO to revise hours."
      : !isMember
        ? "Join this deal before revising banker hours."
        : !isOwner
          ? "Only a deal owner can revise banker hours."
          : null;
    return {
      projectId: project.ProjectId,
      taskId: project.TaskId,
      projectName: project.ProjectName,
      // GroupName on this endpoint is the API-resolved "Live"/"Non-Live" status
      // of the deal's "Live Deal" toggle; prefer it over the company name.
      context: project.GroupName || project.Company || "Energy deal",
      first: toInput(project.LastWeekHours),
      second: toInput(project.WeekendHours),
      third: toInput(project.NextWeekHours),
      originalFirst: project.LastWeekHours,
      originalSecond: project.WeekendHours,
      originalThird: project.NextWeekHours,
      editable,
      lockedReason,
    };
  });
}

function rowChanged(row: StafferDraftRow): boolean {
  return (
    toNumber(row.first) !== row.originalFirst ||
    toNumber(row.second) !== row.originalSecond ||
    toNumber(row.third) !== row.originalThird
  );
}

function rowInvalid(row: StafferDraftRow): boolean {
  return [row.first, row.second, row.third].some((value) => {
    if (!value.trim()) return false;
    const parsed = Number(value);
    return !Number.isFinite(parsed) || parsed < 0;
  });
}

// The web staffer summary keeps bankers without active deals visible with a badge
// instead of hiding them.
function displayName(banker: EnergyStafferBanker): string {
  const name = banker.Level ? `${banker.Name} · ${banker.Level}` : banker.Name;
  return banker.ActiveDeals === false ? `${name} (no active deals)` : name;
}

export function EnergyStafferHoursForm({ isSi, actorUserId }: EnergyStafferHoursFormProps): JSX.Element {
  const [mode, setMode] = useState<StafferMode>("analyst");
  const [bankers, setBankers] = useState<EnergyStafferBanker[]>([]);
  const [selectedBankerId, setSelectedBankerId] = useState<number | null>(null);
  const [rows, setRows] = useState<StafferDraftRow[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const applyResponse = useCallback(
    (response: EnergyHoursResponse) => {
      setRows(buildRows(response.Projects ?? [], actorUserId));
    },
    [actorUserId]
  );

  const loadBanker = useCallback(
    async (analystId: number) => {
      const requestId = ++requestIdRef.current;
      setLoadingRows(true);
      setError(null);
      setSuccess(null);
      try {
        const response = await HoursApi.getEnergyBankerForStaffer(isSi, analystId);
        if (requestId === requestIdRef.current) applyResponse(response);
      } catch (reason: unknown) {
        if (requestId === requestIdRef.current) {
          setRows([]);
          setError(reason instanceof Error ? reason.message : "Could not load this banker's weekly hours.");
        }
      } finally {
        if (requestId === requestIdRef.current) setLoadingRows(false);
      }
    },
    [applyResponse, isSi]
  );

  const loadSummary = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoadingSummary(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await HoursApi.getEnergyStafferSummary(isSi);
      if (requestId !== requestIdRef.current) return;
      const nextBankers = response.JuniorBankers ?? [];
      setBankers(nextBankers);
      const firstBankerId = nextBankers[0]?.UserId ?? null;
      setSelectedBankerId(firstBankerId);
      setRows([]);
      setLoadingSummary(false);
      if (firstBankerId != null) await loadBanker(firstBankerId);
    } catch (reason: unknown) {
      if (requestId === requestIdRef.current) {
        setError(reason instanceof Error ? reason.message : "Could not load the Energy staffer log.");
        setLoadingSummary(false);
      }
    }
  }, [isSi, loadBanker]);

  useEffect(() => {
    void loadSummary();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadSummary]);

  const changedRows = useMemo(() => rows.filter(rowChanged), [rows]);
  const invalid = rows.some(rowInvalid);
  const canSubmit = selectedBankerId != null && changedRows.length > 0 && !invalid && !saving;
  const selectedBanker = bankers.find((banker) => banker.UserId === selectedBankerId);

  const totals = useMemo(
    () =>
      rows.reduce(
        (sum, row) => ({
          weekday: sum.weekday + (toNumber(row.first) ?? 0),
          weekend: sum.weekend + (toNumber(row.second) ?? 0),
          estimate: sum.estimate + (toNumber(row.third) ?? 0),
        }),
        { weekday: 0, weekend: 0, estimate: 0 }
      ),
    [rows]
  );

  const update = (taskId: number, field: "first" | "second" | "third", value: string) => {
    setRows((current) => current.map((row) => (row.taskId === taskId ? { ...row, [field]: value } : row)));
    setSuccess(null);
  };

  const submit = async () => {
    if (!canSubmit || selectedBankerId == null) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await HoursApi.saveEnergyBankerForStaffer(
        isSi,
        changedRows.map((row) => ({
          ProjectId: row.projectId,
          TaskId: row.taskId,
          LastWeekHours: toNumber(row.first),
          WeekendHours: toNumber(row.second),
          NextWeekHours: toNumber(row.third),
          AnalystId: selectedBankerId,
        }))
      );
      if (!response.Projects?.length) {
        // The web staffer modal treats a save response without deals as a failed save.
        setError("Could not save your changes.");
        return;
      }
      applyResponse(response);
      setSuccess(`Weekly hours saved for ${selectedBanker?.Name ?? "the selected banker"}.`);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : "Could not save weekly hours.");
    } finally {
      setSaving(false);
    }
  };

  const analystBody = (): JSX.Element => {
    if (loadingSummary) {
      return (
        <div className="weekly-hours__state" role="status">
          <FluentSpinner label="Loading Energy staffer log…" />
        </div>
      );
    }

    if (error && bankers.length === 0) {
      return (
        <div className="weekly-hours__state" role="alert">
          <p>{error}</p>
          <button type="button" className="weekly-hours__secondary-button" onClick={() => void loadSummary()}>
            Try again
          </button>
        </div>
      );
    }

    return (
      <>
        {bankers.length === 0 ? (
          <div className="weekly-hours__empty">
            No active analysts or associates are available in this staffer view.
          </div>
        ) : (
          <label className="weekly-hours__banker-select">
            <span>Analyst or associate</span>
            <select
              value={selectedBankerId ?? ""}
              onChange={(event) => {
                const analystId = Number(event.target.value);
                setSelectedBankerId(analystId);
                void loadBanker(analystId);
              }}
            >
              {bankers.map((banker) => (
                <option key={banker.UserId} value={banker.UserId}>
                  {displayName(banker)}
                </option>
              ))}
            </select>
          </label>
        )}

        {loadingRows ? (
          <div className="weekly-hours__state" role="status">
            <FluentSpinner label="Loading deal hours…" />
          </div>
        ) : null}

        {!loadingRows && selectedBankerId != null ? (
          <>
            {rows.length === 0 ? (
              <div className="weekly-hours__empty">This banker has no active, eligible Energy deal tasks.</div>
            ) : (
              <HoursTable
                ariaLabel="Banker weekly hours"
                columns={[
                  { short: "M–F", full: STAFFER_LABELS[0] },
                  { short: "S–S", full: STAFFER_LABELS[1] },
                  { short: "Est.", full: STAFFER_LABELS[2] },
                ]}
                rows={rows.map((row): HoursTableRow => {
                  const lockNote = row.lockedReason ? (
                    <p className="weekly-hours__lock-note">{row.lockedReason}</p>
                  ) : null;
                  const validation = rowInvalid(row) ? (
                    <p className="weekly-hours__validation" role="alert">
                      Enter hours greater than or equal to zero.
                    </p>
                  ) : null;
                  return {
                    key: `${row.projectId}-${row.taskId}`,
                    id: row.taskId,
                    name: row.projectName,
                    context: row.context,
                    locked: !row.editable,
                    values: [row.first, row.second, row.third],
                    editable: [row.editable, row.editable, row.editable],
                    ariaLabels: [
                      `${STAFFER_LABELS[0]} for ${row.projectName}`,
                      `${STAFFER_LABELS[1]} for ${row.projectName}`,
                      `${STAFFER_LABELS[2]} for ${row.projectName}`,
                    ],
                    notes:
                      lockNote || validation ? (
                        <>
                          {lockNote}
                          {validation}
                        </>
                      ) : null,
                  };
                })}
                totals={[formatHours(totals.weekday), formatHours(totals.weekend), `${formatHours(totals.estimate)}h`]}
                onValueChange={(taskId, index, value) => update(taskId, FIELDS[index], value)}
              />
            )}
          </>
        ) : null}

        {error ? (
          <div className="weekly-hours__message weekly-hours__message--error" role="alert">
            {error}
          </div>
        ) : null}
        {success ? (
          <div className="weekly-hours__message weekly-hours__message--success" role="status">
            <CheckmarkCircleRegular />
            {success}
          </div>
        ) : null}

        {rows.length > 0 ? (
          <div className="weekly-hours__actions">
            <button
              type="button"
              className="weekly-hours__secondary-button"
              disabled={saving || selectedBankerId == null}
              onClick={() => selectedBankerId != null && void loadBanker(selectedBankerId)}
            >
              Reset
            </button>
            <button
              type="button"
              className="weekly-hours__primary-button"
              disabled={!canSubmit}
              onClick={() => void submit()}
            >
              {saving ? "Saving…" : "Submit changes"}
            </button>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="weekly-hours__form">
      <div className="weekly-hours__intro">
        <h3>{isSi ? "Energy SI staffer revisions" : "Energy staffer revisions"}</h3>
        <p>
          Revise deal-level hour values by analyst or by deal using the same owner, membership, and overdue rules as
          TRIYO Web.
        </p>
      </div>

      <div className="weekly-hours__mode-toggle" role="group" aria-label="Staffer revision mode">
        <button
          type="button"
          className="weekly-hours__secondary-button"
          aria-pressed={mode === "analyst"}
          onClick={() => setMode("analyst")}
        >
          By Jr. banker
        </button>
        <button
          type="button"
          className="weekly-hours__secondary-button"
          aria-pressed={mode === "deal"}
          onClick={() => setMode("deal")}
        >
          By deal
        </button>
      </div>

      {mode === "deal" ? <EnergyStafferDealResourceForm isSi={isSi} actorUserId={actorUserId} /> : analystBody()}
    </div>
  );
}
